"use client"
import React, { useState, useCallback } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './dialog';
import { Globe, AlertCircle, Loader2 } from 'lucide-react';

export interface UrlPickerButtonProps {
  /**
   * Callback function when a file is downloaded from URL
   */
  onFilesSelected: (params: { files: FileList; source?: string }) => void;
  
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  
  /**
   * Custom class name for the button
   */
  className?: string;
  
  /**
   * Button variant
   */
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  
  /**
   * Button text
   */
  children?: React.ReactNode;
}

export function UrlPickerButton({
  onFilesSelected,
  disabled = false,
  className = '',
  variant = "outline",
  children = "Open from URL"
}: UrlPickerButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Function to handle URL download
  const handleUrlDownload = useCallback(async () => {
    if (!urlInput.trim()) {
      setUrlError('Please enter a valid URL');
      return;
    }

    setIsDownloading(true);
    setUrlError(null);

    try {
      // Call the backend API to download from URL
      const response = await fetch('/api/download-from-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: urlInput.trim(),
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to download file from URL');
      }

      // Create a File object from the signed URL
      const fileResponse = await fetch(result.signedUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to fetch the downloaded file');
      }

      const fileBlob = await fileResponse.blob();
      const file = new File([fileBlob], result.originalFilename, {
        type: result.contentType,
      });

      // Convert single file to FileList for compatibility
      const fileList = new DataTransfer();
      fileList.items.add(file);
      onFilesSelected({ files: fileList.files, source: 'url' });

      // Close modal and reset state
      setIsModalOpen(false);
      setUrlInput('');
      setUrlError(null);

    } catch (error) {
      console.error('Error downloading file from URL:', error);
      setUrlError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsDownloading(false);
    }
  }, [urlInput, onFilesSelected]);

  // Function to open URL modal
  const openModal = useCallback(() => {
    if (!disabled) {
      setIsModalOpen(true);
      setUrlInput('');
      setUrlError(null);
    }
  }, [disabled]);

  // Function to close URL modal
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setUrlInput('');
    setUrlError(null);
  }, []);

  return (
    <>
      <Button
        variant={variant}
        onClick={openModal}
        disabled={disabled}
        className={className}
      >
        <Globe className="w-4 h-4 mr-2" />
        {children}
      </Button>

      {/* URL Download Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Open from URL
            </DialogTitle>
            <DialogDescription>
              Enter a URL to download and open a file directly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Input
                type="url"
                placeholder="https://example.com/data.csv"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isDownloading) {
                    handleUrlDownload();
                  }
                }}
                disabled={isDownloading}
                className="w-full"
              />
              {urlError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  {urlError}
                </div>
              )}
            </div>
            
            {/* Example URLs */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium">Examples (click to use):</p>
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => setUrlInput('https://raw.githubusercontent.com/sprabhakaran/datasets/master/Sales.csv')}
                  disabled={isDownloading}
                  className="block w-full text-left text-xs text-aurora-blue hover:text-aurora-blue/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed truncate"
                >
                  Sales data (CSV) - Sample sales records
                </button>
                <button
                  type="button"
                  onClick={() => setUrlInput('https://raw.githubusercontent.com/ourworldincode/currency/main/currencies.json')}
                  disabled={isDownloading}
                  className="block w-full text-left text-xs text-aurora-blue hover:text-aurora-blue/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed truncate"
                >
                  Currency data (JSON) - ISO 4217 currency codes and details
                </button>
                <button
                  type="button"
                  onClick={() => setUrlInput('https://raw.githubusercontent.com/roman-amphitheaters/roman-amphitheaters/main/roman-amphitheaters.csv')}
                  disabled={isDownloading}
                  className="block w-full text-left text-xs text-aurora-blue hover:text-aurora-blue/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed truncate"
                >
                  Roman amphitheaters (CSV) - Historical dataset
                </button>
                <button
                  type="button"
                  onClick={() => setUrlInput('https://api.tvmaze.com/search/shows?q=duck')}
                  disabled={isDownloading}
                  className="block w-full text-left text-xs text-aurora-blue hover:text-aurora-blue/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed truncate"
                >
                  TV shows API (JSON) - Live API data about TV shows
                </button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeModal}
              disabled={isDownloading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUrlDownload}
              disabled={isDownloading || !urlInput.trim()}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4 mr-2" />
                  Download & Open
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}